<template>
  <div 
    v-if="show"
    class="fixed inset-0 bg-gray-900/20 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    @click="handleBackdropClick"
  >
    <div 
      class="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-2xl transform transition-all"
      @click.stop
    >
      <!-- Modal Header -->
      <div class="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
        <div>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
            Add Custom {{ entityTypeLabel }}
          </h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Create a manual entity entry for research
          </p>
        </div>
        <button
          @click="$emit('cancel')"
          :disabled="loading"
          class="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
        >
          <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <!-- Modal Body -->
      <div class="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
        <!-- Error Message -->
        <div v-if="errorMessage" class="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4">
          <div class="flex items-start gap-3">
            <svg class="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <div class="flex-1">
              <p class="text-sm font-medium text-red-800 dark:text-red-300">Failed to add entity</p>
              <p class="text-sm text-red-700 dark:text-red-400 mt-1">{{ errorMessage }}</p>
            </div>
            <button @click="errorMessage = ''" class="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Entity Name (Required) -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {{ entityTypeLabel }} Name <span class="text-red-500">*</span>
          </label>
          <input
            v-model="formData.name"
            type="text"
            :placeholder="`Enter ${entityTypeLabel.toLowerCase()} name`"
            class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            :disabled="loading"
          />
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Required field</p>
        </div>

        <!-- Description (Optional) -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Description
          </label>
          <textarea
            v-model="formData.description"
            rows="3"
            :placeholder="`Brief description of the ${entityTypeLabel.toLowerCase()}`"
            class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all"
            :disabled="loading"
          ></textarea>
        </div>

        <!-- Company-specific fields -->
        <template v-if="entityType === 'company'">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Headquarters
              </label>
              <input
                v-model="formData.headquarters"
                type="text"
                placeholder="City, Country"
                class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                :disabled="loading"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Industry
              </label>
              <input
                v-model="formData.industry"
                type="text"
                placeholder="e.g., Technology, Finance"
                class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                :disabled="loading"
              />
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Website
            </label>
            <input
              v-model="formData.website"
              type="text"
              placeholder="https://example.com"
              class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              :disabled="loading"
            />
          </div>
        </template>

        <!-- Person-specific fields -->
        <template v-if="entityType === 'person'">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Job Title
              </label>
              <input
                v-model="formData.jobTitle"
                type="text"
                placeholder="e.g., CEO, Director"
                class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                :disabled="loading"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Company
              </label>
              <input
                v-model="formData.company"
                type="text"
                placeholder="Current employer"
                class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                :disabled="loading"
              />
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Location
            </label>
            <input
              v-model="formData.location"
              type="text"
              placeholder="City, Country"
              class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              :disabled="loading"
            />
          </div>
        </template>
      </div>

      <!-- Modal Footer -->
      <div class="flex items-center justify-end gap-3 p-6 bg-gray-50 dark:bg-gray-800/50 rounded-b-2xl border-t border-gray-200 dark:border-gray-700">
        <button
          @click="$emit('cancel')"
          :disabled="loading"
          class="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          @click="handleSubmit"
          :disabled="!isValid || loading"
          class="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center gap-2"
        >
          <div v-if="loading" class="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
          <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
          {{ loading ? 'Adding...' : 'Add Entity' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  entityType: {
    type: String,
    required: true,
    validator: (value) => ['company', 'person', 'organization'].includes(value)
  },
  companyInfo: {
    type: Object,
    default: () => ({})
  },
  personInfo: {
    type: Object,
    default: () => ({})
  },
  loading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['confirm', 'cancel'])

// Form data
const formData = ref({
  name: '',
  description: '',
  // Company fields
  headquarters: '',
  industry: '',
  website: '',
  // Person fields
  jobTitle: '',
  company: '',
  location: ''
})

// Error message state
const errorMessage = ref('')

// Computed properties
const entityTypeLabel = computed(() => {
  const labels = {
    company: 'Company',
    person: 'Person',
    organization: 'Organization'
  }
  return labels[props.entityType] || 'Entity'
})

const isValid = computed(() => {
  return formData.value.name && formData.value.name.trim().length > 0
})

// Pre-fill form data when modal opens
watch(() => props.show, (newValue) => {
  if (newValue) {
    // Pre-fill from search context
    if (props.entityType === 'company' && props.companyInfo) {
      formData.value.headquarters = props.companyInfo.headquarters || ''
      formData.value.industry = props.companyInfo.industry || ''
      formData.value.website = props.companyInfo.website || ''
    } else if (props.entityType === 'person' && props.personInfo) {
      formData.value.location = props.personInfo.location || ''
      formData.value.jobTitle = props.personInfo.jobTitle || ''
      formData.value.company = props.personInfo.company || ''
    }
    // Clear error message when reopening
    errorMessage.value = ''
  }
})

// Handle form submission
function handleSubmit() {
  if (!isValid.value) return
  
  // Clear any previous errors
  errorMessage.value = ''
  
  // Build additionalInfo based on entity type
  const additionalInfo = {}
  
  if (props.entityType === 'company') {
    if (formData.value.headquarters) additionalInfo.headquarters = formData.value.headquarters
    if (formData.value.industry) additionalInfo.industry = formData.value.industry
    if (formData.value.website) additionalInfo.website = formData.value.website
  } else if (props.entityType === 'person') {
    if (formData.value.jobTitle) additionalInfo.jobTitle = formData.value.jobTitle
    if (formData.value.company) additionalInfo.company = formData.value.company
    if (formData.value.location) additionalInfo.location = formData.value.location
  }
  
  // Emit confirm with form data
  emit('confirm', {
    name: formData.value.name.trim(),
    description: formData.value.description.trim() || undefined,
    additionalInfo: Object.keys(additionalInfo).length > 0 ? additionalInfo : undefined
  })
}

// Handle backdrop click
function handleBackdropClick() {
  if (!props.loading) {
    emit('cancel')
  }
}

// Expose method to set error message
function setError(message) {
  errorMessage.value = message
}

// Expose method to reset form
function resetForm() {
  formData.value = {
    name: '',
    description: '',
    headquarters: '',
    industry: '',
    website: '',
    jobTitle: '',
    company: '',
    location: ''
  }
  errorMessage.value = ''
}

defineExpose({
  setError,
  resetForm
})
</script>
